import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import  { Alert, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import HelperMetiers360 from '../../../services/HelpersMetiers360';
import useDebounceSearch from '../../../hooks/useDebounchSearch';
import DynamicTable from '../../widgets/dynamicTable/DynamicTable';
import AuthorizationChecker from '../../../services/AuthorizationChecker';
import { useStoreState } from 'easy-peasy';
import { Link } from 'react-router-dom';

const HeadsetAccessibleVideos = ({ uploadedAndAccessibleVideos }) => {

    const videosSlugs = useStoreState(actions => actions.actionSlugs.actionSlugsDispatcher('videos'));
    const [filteredVideos, setFilteredVideos] = useState(uploadedAndAccessibleVideos);
    const { searchInput, search } = useDebounceSearch({ placeholder: "Rechercher une expérience immersive" });

    useEffect(() => {
        setFilteredVideos(uploadedAndAccessibleVideos.filter(video => 
            HelperMetiers360.isSearchInText(search, video.name)
        ));
    }, [search])

    const displayThumbnail = (video) => 
        <Image src={video.thumbnail} className="img-overlay" thumbnail />;

    const displayUploadedVideo = (video) => 
        video.uploaded 
            ? <>Oui</> 
            : <OverlayTrigger placement="left" 
                overlay={<Tooltip>
                    Veuillez mettre à jour votre casque via le Loader pour charger cette expérience immersive.
                </Tooltip>}>
                <span>
                    Non<i className="fas fa-info-circle fa-lg ms-2"></i>
                </span>
            </OverlayTrigger>;

    const displayFullyUploadedVideo = (video) =>
        !video.uploaded ? <span>-</span> :
        video.fullyUploaded
            ? <>Oui</>
            : <OverlayTrigger placement="left" 
                overlay={<Tooltip>
                    Le téléchargement de cette expérience immersive a rencontré un problème.
                    Veuillez mettre à jour votre casque via le Loader pour finaliser le téléchargement.
                </Tooltip>}>
                <span>
                        <i className="fas fa-exclamation-triangle text-danger me-2"> </i>
                        Non
                </span>
            </OverlayTrigger>;


    const sortType = [
        {
            value : 'thumbnail', label : 'Vignette',
            test: HelperMetiers360.isArrayContainsValue(filteredVideos, "thumbnail"),
            display: (video) => displayThumbnail(video),
            flatDisplay: (video) => video.thumbnail ?? '-'
        },
        {
            value : 'name', label : 'Nom',
            test : HelperMetiers360.isArrayContainsValue(filteredVideos, "name"),
            method: (a,b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
            display: (video) => <Link to={videosSlugs.readOne.replace(':uniqueId', video.uniqueId)} target='_blank'>
                {video.name}
            </Link>,
            flatDisplay: (video) => video.name
        },
        {
            value : 'accessible', label : 'Accessible',
            test : AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(filteredVideos, "accessible"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.accessible, b.accessible),
            display: (video) => video.accessible ? 'Oui' : 'Non'
        },
        {
            value : 'uploaded', label : 'Chargée',
            test : HelperMetiers360.isArrayContainsValue(filteredVideos, "uploaded"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.uploaded, b.uploaded),
            display: (video) => displayUploadedVideo(video),
            flatDisplay: (video) => video.uploaded ? 'Oui' : 'Non'
        },
        {
            value: 'fullyUploaded', label: 'Totalement chargée',
            test: HelperMetiers360.isArrayContainsValue(filteredVideos, "fullyUploaded"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.fullyUploaded, b.fullyUploaded),
            display: (video) => displayFullyUploadedVideo(video),
            flatDisplay: (video) => video.fullyUploaded ? 'Oui' : 'Non'
        },
        {
            value: 'uploadedType', label: 'Format chargé',
            test: HelperMetiers360.isArrayContainsValue(filteredVideos, "uploadedType"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.uploadedType, b.uploadedType),
            display: (video) => video.uploadedType ?? '-',
        }
    ];

    const backgroundColorVideoLine = (video) => 
        AuthorizationChecker.isAdmin() && !video.accessible && video.uploaded
            ? '#FFA7A9'
            : null;
    
    const tableFilteredWithAlertColors = filteredVideos.map(video => {
        return {
            ...video,
            background: backgroundColorVideoLine(video)
        }
    })

    return uploadedAndAccessibleVideos?.length > 0
        ? <>
            <div className='w-100' style={{marginBottom: "-2em"}}>{searchInput}</div>
            { filteredVideos.length > 0
                ? <DynamicTable
                    contentTable={tableFilteredWithAlertColors}
                    contentSort={sortType}
                    valueInitSort="name"
                    index='uniqueId' 
                    filename="casque_experiences_immersives_chargees"
                />
                : <Alert variant='warning' className='w-100'>Aucune expérience immersive ne correspond à votre recherche</Alert>
            }
        </>
        : <Alert variant='danger' className='w-100'>Pas d'expérience immersive disponible</Alert>;
}

HeadsetAccessibleVideos.propTypes = {
    uploadedAndAccessibleVideos: PropTypes.array.isRequired,
}

export default HeadsetAccessibleVideos;